// src/components/GardenCropsPlanView.js
import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import PlotCard from "./PlotCard";

const GardenCropsPlanView = ({ 
  cropsByPlot, 
  garden_uuid, 
  year, 
  onNavigate, 
  onPlanCrop, 
  onAssignFruit, 
  onManageCompost,
  onEditPlanted
}) => {
  const [selectedPlotType, setSelectedPlotType] = useState('vegetable-bed');

  // Filter cropsByPlot based on selected plot type
  const filteredCropsByPlot = useMemo(() => {
    return cropsByPlot.filter(({ plot }) => {
      const plotTypeName = plot.plot_type?.name?.toLowerCase();
      switch (selectedPlotType) {
        case 'vegetable-bed':
          return plotTypeName === 'vegetable bed';
        case 'fruit-tree':
          return plotTypeName === 'fruit tree';
        case 'compost-bin':
          return plotTypeName === 'compost bin';
        default:
          return true;
      }
    });
  }, [cropsByPlot, selectedPlotType]);

  const handlePlotTypeChange = (event, newValue) => {
    if (newValue !== null) {
      setSelectedPlotType(newValue);
    }
  };

  const handleAssignFruitFromCard = (plot) => {
    // Call the parent's onAssignFruit with the specific plot
    onAssignFruit(plot);
  };

  const getActionButton = () => {
    switch (selectedPlotType) {
      case 'vegetable-bed':
        return (
          <Button
            variant="outlined"
            onClick={onPlanCrop}
            className="vintage-button"
          >
            ✚ Plan New Crop
          </Button>
        );
      case 'fruit-tree':
        // No action button for fruit trees - use card buttons instead
        return null;
      case 'compost-bin':
        return (
          <Button
            variant="outlined"
            onClick={onManageCompost}
            className="vintage-button"
          >
            ✚ Manage Compost
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      {/* Plot Type Filter Toggle */}
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={selectedPlotType}
          exclusive
          onChange={handlePlotTypeChange}
          size="small"
        >
          <ToggleButton value="vegetable-bed">Vegetable Beds</ToggleButton>
          <ToggleButton value="fruit-tree">Fruit Trees</ToggleButton>
          {/* TODO: Re-enable when compost management is ready */}
          {/* <ToggleButton value="compost-bin">Compost Bins</ToggleButton> */}
        </ToggleButtonGroup>
      </Box>

      {/* Action Button */}
      <Box sx={{ mb: 2 }}>
        {getActionButton()}
      </Box>

      {/* Plot Cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {filteredCropsByPlot.length === 0 ? (
          <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
            No {selectedPlotType.replace('-', ' ')} plots found.
          </Typography>
        ) : (
          filteredCropsByPlot.map(({ plot, crops }) => (
            <Box key={plot.plot_uuid} className="plot-card-container">
              <PlotCard
                plot={plot}
                crops={crops}
                year={year}
                onClick={() =>
                  onNavigate(
                    `/gardens/${garden_uuid}/plots/${plot.plot_uuid}/detail/${year}`
                  )
                }
                onAssignFruit={selectedPlotType === 'fruit-tree' ? handleAssignFruitFromCard : null}
                onEditPlanted={onEditPlanted}
              />
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default GardenCropsPlanView;
